/**
 * AI tiering + grounding helpers — pure. The HTTP client lives in the api; this
 * module decides WHICH model tier a task uses and builds the grounded prompts,
 * plus the deterministic extractive fallback used when AI is disabled.
 *
 * Tiering: complex/judgement tasks use the LARGE (reasoning) model; simple,
 * mechanical tasks use the SMALL (fast) model.
 */

import type { Citation } from './rag.js';

export type AiTier = 'reasoning' | 'fast';

export type AiTask =
  | 'doctrine-answer' // synthesise a grounded answer from doctrine snippets
  | 'handover-draft' // draft a handover/SITREP narrative from structured fields
  | 'briefing' // situational briefing from structured context
  | 'sitrep-polish' // tighten an existing deterministic SITREP
  | 'classify' // map free text to a controlled value
  | 'short-rephrase'; // one-line tidy-up

/** Complex tasks → large model; simple/mechanical tasks → small model. */
export const TASK_TIER: Record<AiTask, AiTier> = {
  'doctrine-answer': 'reasoning',
  'handover-draft': 'reasoning',
  briefing: 'reasoning',
  'sitrep-polish': 'fast',
  classify: 'fast',
  'short-rephrase': 'fast',
};

export const tierForTask = (task: AiTask): AiTier => TASK_TIER[task];

/**
 * The grounding-contract system prompt: the model may only use the supplied
 * material, must cite sources by title, must not invent doctrine/facts, and must
 * say so when the material is insufficient (no-source ⇒ no-answer).
 */
export function groundedSystemPrompt(): string {
  return [
    'You are Duty, an advisory assistant for NSW emergency-services officers.',
    'Answer ONLY using the provided doctrine snippets and incident context.',
    'Do not introduce facts, numbers, or doctrine that are not in the provided material.',
    'Cite the source document title in parentheses after each claim.',
    'If the provided material does not answer the question, say: "No applicable doctrine found for this in scope." and stop.',
    'Use advisory language ("Consider…", "Appears to…"); never issue commands.',
    'Be concise.',
  ].join(' ');
}

/** Build the user prompt: the question plus the (already filtered) snippets. */
export function groundedUserPrompt(query: string, citations: readonly Citation[]): string {
  const sources = citations
    .map((c, i) => `[${i + 1}] (${c.docTitle}) ${c.snippet}`)
    .join('\n');
  return `Question: ${query}\n\nApplicable doctrine snippets:\n${sources}`;
}

/**
 * Deterministic extractive answer used when AI is disabled (or as the template
 * the LLM rephrases). Returns null when there are no sources (no-answer).
 */
export function composeExtractiveAnswer(citations: readonly Citation[]): string | null {
  if (citations.length === 0) return null;
  const bullets = citations.map((c) => `• ${c.snippet} (${c.docTitle})`).join('\n');
  return `Based on applicable doctrine:\n${bullets}`;
}

export const NO_DOCTRINE_ANSWER = 'No applicable doctrine found for this in scope.';
